/**
 * Context enrichment for Wisetap rows.
 *
 * - Device fields resolve synchronously at module load (getVersion/getModel/... are
 *   sync); `unique_device_id` is async so it's filled in by initDeviceContext().
 * - User fields are read live from the Redux store on every snapshot().
 * - Location is cached (last-known) and refreshed opportunistically, never
 *   fetched per-event (GPS-per-event would be a battery/latency non-starter).
 */
import { Platform } from "react-native";
import {
  getVersion,
  getSystemVersion,
  getModel,
  getUniqueId,
} from "react-native-device-info";
import { store } from "@store";
import { appEnv } from "@config";
import getOrCreateWebDeviceId from "@utils/getOrCreateWebDeviceId";
// @hooks/useLocation is a plain async fn (misnamed); alias off the `use*` prefix
// so it isn't treated as a React hook by rules-of-hooks.
import getLatLng from "@hooks/useLocation";
import type { ContextSnapshot, DeviceContext } from "./types";

const APP_NAME = "partner-db";
const LOCATION_TTL_MS = 60 * 1000;

type LatLng = { lat: number | null; lng: number | null };

const buildSyncDevice = (): DeviceContext => ({
  app_name: APP_NAME,
  unique_device_id: null,
  platform: Platform.OS,
  app_version: getVersion(),
  os_version: `${Platform.OS === "ios" ? "iOS" : "Android"} ${getSystemVersion()}`,
  device_model: getModel(),
});

let deviceCtx: DeviceContext = buildSyncDevice();
let deviceIdResolved = false;

let lastLocation: LatLng = { lat: null, lng: null };
let lastLocationAt = 0;
let refreshingLocation = false;

export const normalizePhone = (phone: unknown): string | null => {
  if (!phone) return null;
  const digits = String(phone).replace(/\D/g, "");
  if (!digits) return null;
  // National number only — strip any country code (e.g. 91) by keeping last 10 digits.
  return digits.length > 10 ? digits.slice(-10) : digits;
};

/** Resolve the async unique_device_id once and cache it. Safe to call repeatedly. */
export const initDeviceContext = async (): Promise<DeviceContext> => {
  if (deviceIdResolved) return deviceCtx;
  try {
    const id = (await getUniqueId()) || (await getOrCreateWebDeviceId());
    deviceCtx = { ...deviceCtx, unique_device_id: id || null };
  } catch {
    const web = await getOrCreateWebDeviceId();
    deviceCtx = { ...deviceCtx, unique_device_id: web || null };
  }
  deviceIdResolved = true;
  return deviceCtx;
};

const refreshLocation = (): void => {
  if (refreshingLocation) return;
  refreshingLocation = true;
  getLatLng()
    .then((loc: LatLng) => {
      if (loc && (loc.lat != null || loc.lng != null)) {
        lastLocation = { lat: loc.lat, lng: loc.lng };
        lastLocationAt = Date.now();
      }
    })
    .catch(() => {})
    .finally(() => {
      refreshingLocation = false;
    });
};

export const getCachedLocation = (): LatLng => {
  if (Date.now() - lastLocationAt > LOCATION_TTL_MS) refreshLocation(); // non-blocking
  return lastLocation;
};

const getUser = (): Record<string, unknown> => {
  try {
    const state = store.getState() as {
      auth?: { user?: Record<string, unknown> };
    };
    return state?.auth?.user || {};
  } catch {
    return {};
  }
};

/** A full context snapshot for one row: device + user + location + env. */
export const snapshot = (): ContextSnapshot => {
  const user = getUser();
  const loc = getCachedLocation();
  // partner-db keys on the CP DB's own id (cp_db_id), NOT the team-leader id
  // (data.id) used by Sentry/FB/CleverTap. No user_id fallback — a CP Leader
  // (cp_db_id empty) yields a null external_user_id rather than a mismatched key.
  const rawId = user.cp_db_id;
  return {
    ...deviceCtx,
    external_user_id: rawId ? String(rawId) : null,
    // CP DBs (cp_db_id truthy) are keyed on their own phone; CP Leaders on the
    // account phone. Mirrors the isCpDb split in Home/Header.js + CpDbHOC.
    phone_number: normalizePhone(
      user.cp_db_id ? user.db_phone_number : user.phone_number,
    ),
    lat: loc.lat,
    lng: loc.lng,
    is_test: appEnv !== "production",
  };
};
